import { useState, useEffect } from "react";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import Dialog from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";
import Heading from "../Typography/Heading";
import { styled } from "@mui/material/styles";
import SurveyButton from "../UserInput/Buttons/SurveyButton";
import CopyButton from "../UserInput/Buttons/CopyButton";
import isEmail from "validator/lib/isEmail";
import { removeAnchorLink } from "../../utils";

export default function RememberYourResults({
    surveyResults, //TODO only the emailed_to property is used to determine if it's already been emailed, just pass in a boolean instead
    onClose,
    show,
    themeColors,
    emailResults,
}) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const [open, setOpen] = useState(false);
    const [showCopyTooltip, setShowCopyTooltip] = useState(false);
    const [email, setEmail] = useState("");
    const [agreeToTerms, setAgreeToTerms] = useState(false);
    const [error, setError] = useState("");

    const handleClose = () => {
        setOpen(false);
        onClose(false);
    };

    useEffect(() => {
        setOpen(show);
    }, [show]);

    useEffect(() => {
        if (showCopyTooltip) {
            const timeout = setTimeout(() => {
                setShowCopyTooltip(false);
                clearTimeout(timeout);
            }, 2000);
        }
    }, [showCopyTooltip]);

    async function submitEmail() {
        if (!email) {
            setError("Please specify your email address.");
            return;
        }
        if (!isEmail(email)) {
            setError("Please enter a valid email address.");
            return;
        }
        if (!agreeToTerms) {
            setError("Please agree to terms and conditions.");
            return;
        }

        //todo: error msg
        if (emailResults(email)) {
            handleClose();
        }
    }

    const StyledTypography = styled(Typography)({
        color: "#FFFFFF",
        fontSize: "20px",
        letterSpacing: "-0.3px",
    });

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                className="bg-transparent overflow-hidden border-none"
                slotProps={{
                    paper: {
                        "aria-labelledby": null,
                        style: {
                            borderRadius: 16,
                            maxWidth: fullScreen ? "90%" : "57%",
                            textAlign: fullScreen ? "center" : "left",
                            border: `solid 16px ${themeColors.primaryColor}`,
                        },
                    },
                }}
            >
                <div
                    style={{ background: themeColors.primaryColor }}
                    className="py-4 px-2 md:px-8 text-xs md:text-base"
                >
                    <Heading
                        component={"h2"}
                        fontSize={32}
                        mb="33px"
                        className="text-center font-semibold text-white pb-6"
                    >
                        Remember your results
                    </Heading>
                    <div className="text-white">
                        <StyledTypography mb="44px">
                            If you'd like to come back to this page later, bookmark the following link or save it in a
                            separate document.
                        </StyledTypography>
                        <StyledTypography mb="54px">
                            Simply return to this link anytime and your {themeColors.metricName} score will be waiting
                            for you.
                        </StyledTypography>
                        {surveyResults.emailed_to ? (
                            <div
                                className="p-4 rounded-lg  text-white text-center text-[14pt]"
                                style={{
                                    background: themeColors.buttonBackgroundColor,
                                    color: themeColors.buttonTextColor,
                                }}
                            >
                                <StyledTypography><b>You already requested a copy of your results</b></StyledTypography>
                            </div>
                        ) : (
                            <>
                                <div className="flex md:flex-row flex-col items-center mb-[48px]">
                                    <StyledTypography className="mr-[15px] xl:min-w-fit">
                                        Email/copy the link to my {themeColors.metricName} score
                                    </StyledTypography>

                                    <div className="py-2 px-3 rounded-lg bg-white flex space-x-2 mr-[9px] w-full ml-auto">
                                        <EmailOutlinedIcon className="text-slate-500"></EmailOutlinedIcon>
                                        <div className="border-r border-slate-300">&nbsp;</div>
                                        <input
                                            aria-label="email"
                                            value={email}
                                            onChange={(event) => setEmail(event.target.value)}
                                            type="text"
                                            className="outline-none text-slate-600 w-full"
                                            placeholder="example@email.com"
                                        />
                                    </div>
                                    <CopyButton
                                        textToCopy={removeAnchorLink(window.location.href)}
                                        colorTheme={themeColors}
                                    />
                                </div>
                                <div className="mb-[59px]">
                                    <label htmlFor="agree-to-terms" className="flex items-start space-x-2">
                                        <input
                                            id="agree-to-terms"
                                            className="mt-[5px]"
                                            type="checkbox"
                                            value={agreeToTerms}
                                            onChange={(event) => setAgreeToTerms(event.target.checked)}
                                        />
                                        <StyledTypography className="cursor-pointer font-normal">
                                            I understand that my email will not be collected or used for any other
                                            purpose.
                                        </StyledTypography>
                                    </label>
                                </div>
                                {error && (
                                    <div className="bg-red-500 text-white px-4 py-3 rounded-lg mb-5">
                                        <StyledTypography>{error}</StyledTypography>
                                    </div>
                                )}
                                <div className="flex justify-center gap-10">
                                    <SurveyButton
                                        size={{ fontSize: "14pt", paddingX: "14px", paddingY: "7px" }}
                                        handleOnClick={submitEmail}
                                        colorTheme={themeColors}
                                    >
                                        Submit
                                    </SurveyButton>
                                    <SurveyButton
                                        forceRounded={themeColors.roundedButtons}
                                        size={{ fontSize: "14pt", paddingX: "14px", paddingY: "7px" }}
                                        handleOnClick={handleClose}
                                    >
                                        Cancel
                                    </SurveyButton>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </Dialog>
        </>
    );
}
